import { Flags } from '@fullstory/client-api';
import type { PageModel } from '@fullstory/playback-model';
import { useMergedRef } from '@fullstory/react-hooks';
import { useLayoutObservable } from '@fullstory/react-rxjs';
import { head } from 'lodash';
import React, {
  ComponentPropsWithoutRef,
  forwardRef,
  memo,
  useCallback,
  useRef,
  useState,
} from 'react';
import { map, mergeMap, Observable } from 'rxjs';
import {
  LoadEndResult,
  LoadResult,
  PlayEndResult,
  PlayResult,
  RendererClient,
  SeekEndResult,
  SeekResult,
  toObservable,
  useClient,
  usePlayback,
} from './internal';

// Re-export type for external use
export type {
  LoadEndResult,
  LoadResult,
  PlayEndResult,
  PlayResult,
  RendererClient,
  SeekEndResult,
  SeekResult,
} from './internal';
export interface RendererContext {
  flags: Flags;
}

type DivProps = Omit<ComponentPropsWithoutRef<'div'>, 'onLoad' | 'onPlay'>;

export interface RendererProps extends DivProps {
  /**
   * Useful for placing absolutely positioned elements, such as overlays, above the renderer
   */
  children?: React.ReactNode;
  /**
   * Context for the renderer including flags, etc.
   */
  ctx: RendererContext;
  /**
   * When true, suspends CPU intensive processes and ignores {@link offsetMs} changes.
   * This is useful when the renderer is not visible, such as in a background tab.
   */
  isSuspended?: boolean;
  /**
   * Offset from the page start time (also known as `When`) to which the renderer should play or seek.
   *
   * Accepts either a number or an Observable<number>. An Observable is necessary when the value
   * is expected to change _very_ frequently, such as every animation frame.
   * This is the case in normal playback.
   */
  offsetMs?: number | Observable<number | undefined>;
  /**
   * The page iframe finished loading successfully.
   */
  onLoad?: (client: RendererClient, result: LoadResult) => void;
  /**
   * The page iframe finished loading successfully or unsuccessfully.
   */
  onLoadEnd?: (result: LoadEndResult) => void;
  /**
   * The page iframe finished loading unsuccessfully due to an error.
   */
  onLoadError?: (e: Error) => void;
  /**
   * The page iframe started loading.
   */
  onLoadStart?: () => void;
  /**
   * The renderer finished playing the page to an initial timestamp successfully.
   */
  onPlay?: (result: PlayResult) => void;
  /**
   * The renderer finished playing the page to an initial timestamp successfully or unsuccessfully.
   */
  onPlayEnd?: (result: PlayEndResult) => void;
  /**
   * The renderer finished playing the page to an initial timestamp unsuccessfully due to an error.
   */
  onPlayError?: (e: Error) => void;
  /**
   * The renderer started playing the page to an initial timestamp.
   */
  onPlayStart?: (offsetMs: number) => void;
  /**
   * The renderer finished seeking the page to a new timestamp successfully.
   */
  onSeek?: (result: SeekResult) => void;
  /**
   * The renderer finished seeking the page to a different timestamp successfully or unsuccessfully.
   */
  onSeekEnd?: (result: SeekEndResult) => void;
  /**
   * The renderer finished seeking the page to a different timestamp unsuccessfully due to an error.
   */
  onSeekError?: (e: Error) => void;
  /**
   * The renderer started seeking the page to a different timestamp.
   */
  onSeekStart?: (offsetMs: number) => void;
  /**
   * The org ID corresponding to the session containing the page.
   */
  orgId: string;
  /**
   * The page to play.
   * It is required for the renderer to actually play, but
   * `null` and `undefined` are accepted as a convenience
   * because pages generally load asynchronously.
   */
  page?: Readonly<PageModel> | null;
  /**
   * Styles to apply to the root div wrapping the renderer iframe.
   */
  style?: React.CSSProperties;
  /**
   * A title to apply to the renderer iframe.
   */
  title?: string;
}

export const Renderer = memo(
  forwardRef<HTMLIFrameElement, RendererProps>(
    (
      {
        children,
        ctx,
        isSuspended,
        offsetMs,
        onLoad,
        onLoadEnd,
        onLoadError,
        onLoadStart,
        onPlay,
        onPlayEnd,
        onPlayError,
        onPlayStart,
        onSeek,
        onSeekEnd,
        onSeekError,
        onSeekStart,
        orgId,
        page,
        style = {},
        title,
        ...rest
      },
      forwardedRef,
    ) => {
      const { flags } = ctx;

      // Coerce the offsetMs prop to an Observable
      // Note the use of useLayoutObservable to render faster.
      const offsetMs$ = useLayoutObservable(
        source$ =>
          source$.pipe(
            map(head),
            mergeMap(toObservable),
            map(value => value),
          ),
        [offsetMs],
      ) as Observable<number | undefined>;

      // In some cases, we need to trigger re-renders via a state update
      // TODO(jordanhurt) - this could be removed by returning a callback from
      // useClient that is invoked in the ref callback and a separate useEffect call
      const [, setRenderCount] = useState<number>(0);
      const rerender = useCallback(() => setRenderCount(prev => prev + 1), []);

      // Ref to hold our iframe element
      const iframeRef = useRef<HTMLIFrameElement | null>(null);

      // We need to trigger an update when we receive a new iframe element
      const iframeRefCallback = useCallback(
        (iframe: HTMLIFrameElement | null) => {
          iframeRef.current = iframe;
          rerender();
        },
        [rerender],
      );

      // Create the renderer client to manage our iframe
      const client = useClient(
        {
          iframe: iframeRef.current,
          offsetMs$,
          flags,
          orgId,
          page,
        },
        {
          onLoad,
          onLoadEnd,
          onLoadError,
          onLoadStart,
          onPlay,
          onPlayEnd,
          onPlayError,
          onPlayStart,
        },
      );

      // Start and seek playback whenever the page or offset changes
      usePlayback(
        {
          client,
          isSuspended,
          offsetMs$,
        },
        {
          onSeek,
          onSeekEnd,
          onSeekError,
          onSeekStart,
        },
      );

      return (
        <div
          {...rest}
          style={{
            flexGrow: 1,
            height: '100%',
            position: 'relative',
            // Their styles can override defaults
            ...style,
          }}
        >
          <iframe
            ref={useMergedRef(iframeRefCallback, forwardedRef)}
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              borderWidth: 0,
            }}
            title={title}
          />
          {children}
        </div>
      );
    },
  ),
);

Renderer.displayName = 'Renderer';
